import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { guardWrite } from '../../../lib/modeGuard';
import { useAppMode } from '../../../contexts/AppModeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { capabilitiesFor } from '../../../lib/permissions';
import * as schedulerApi from '../../../lib/api/schedulerApi';
import * as patientApi from '../../../lib/api/patientApi';
import {
  Search, User, Calendar, Clock, CheckCircle2, AlertTriangle,
  Sparkles, MapPin, Loader2, ArrowRight, DollarSign, RefreshCw, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const MOCK_PATIENTS = [
  { id: 'P001', name: 'John Smith', dob: '03/15/1962', phone: '(555) 123-4567', insurance: 'Active', lastVisit: '2 weeks ago', noShowRisk: 5, balance: 0 },
  { id: 'P002', name: 'Maria Garcia', dob: '07/22/1985', phone: '(555) 234-5678', insurance: 'Active', lastVisit: '1 month ago', noShowRisk: 12, balance: 0 },
  { id: 'P004', name: 'Emily Davis', dob: '11/30/1978', phone: '(555) 345-6789', insurance: 'Active', lastVisit: '3 months ago', noShowRisk: 22, balance: 20 },
  { id: 'P005', name: 'James Taylor', dob: '09/08/1970', phone: '(555) 456-7890', insurance: 'Active', lastVisit: '6 weeks ago', noShowRisk: 35, balance: 0, careGap: 'Annual Wellness due' },
];

// Vendor-canonical visit-type chip set. Driven by API in real mode; these
// labels mirror the 5 types the vendor UI exposes (§19 M9e / spec §4).
const DEMO_APT_TYPES = [
  { id: 'consultation',     label: 'Consultation',    duration: 30 },
  { id: 'followup',         label: 'Follow-up',       duration: 30 },
  { id: 'general_checkup',  label: 'General Checkup', duration: 45 },
  { id: 'procedure',        label: 'Procedure',       duration: 60 },
  { id: 'urgent',           label: 'Urgent',          duration: 20 },
];

const DEMO_PROVIDERS = [
  { id: 'dr-chen', name: 'Dr. Chen', specialty: 'Internal Medicine' },
  { id: 'dr-wilson', name: 'Dr. Wilson', specialty: 'Family Medicine' },
];

const DEMO_SLOTS = [
  { time: '11:30 AM', provider: 'dr-chen', recommended: true, reason: 'Matches morning preference, low no-show time' },
  { time: '2:30 PM', provider: 'dr-wilson', recommended: false },
  { time: '3:30 PM', provider: 'dr-chen', recommended: false },
  { time: '4:00 PM', provider: 'dr-wilson', recommended: false },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const pickDate = (selected) => (selected && /^\d{4}-\d{2}-\d{2}$/.test(selected) ? selected : todayIso());

const normalizeVisitType = (v) => ({
  id: v.id || v._id,
  label: v.name || v.label,
  duration: v.duration_min || v.duration || 30,
});

const normalizeEntity = (e) => ({
  id: e.id || e._id,
  name: e.name || e.full_name,
  specialty: e.specialty || e.role || '',
});

const normalizeSlot = (s) => ({
  time: s.start_label || s.start || s.time,
  provider: s.entity_id || s.provider_id || s.provider,
  recommended: !!s.recommended,
  reason: s.reason || null,
  rawStart: s.start,
  rawEnd: s.end,
});

const BookingModal = ({ open, onOpenChange, prefillTime, prefillProviderId, prefillLocationId, prefillIsWalkin = false, selectedDate, allowBooking: allowBookingProp }) => {
  const { isDemo } = useAppMode();
  const { user } = useAuth();
  const { canSchedulerWrite } = capabilitiesFor(user);
  const allowBooking = allowBookingProp !== undefined ? allowBookingProp : (isDemo || canSchedulerWrite);
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [aptType, setAptType] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(prefillLocationId || '');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [telehealth, setTelehealth] = useState(false);
  const [isWalkIn, setIsWalkIn] = useState(!!prefillIsWalkin);
  const [customDuration, setCustomDuration] = useState(null);
  const [booking, setBooking] = useState(false);

  // Re-apply prefillIsWalkin whenever the modal is (re)opened with a new
  // intent (Walk-in CTA vs. New Appointment CTA). This keeps the toggle in
  // sync with the opener button without stale state between sessions.
  useEffect(() => {
    if (open) setIsWalkIn(!!prefillIsWalkin);
  }, [open, prefillIsWalkin]);

  // API-backed data
  const [aptTypes, setAptTypes] = useState(isDemo ? DEMO_APT_TYPES : []);
  const [providers, setProviders] = useState(isDemo ? DEMO_PROVIDERS : []);
  const [slots, setSlots] = useState(isDemo ? DEMO_SLOTS : []);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  // Available locations (vendor §17 requires BOTH entity_id AND location_id)
  const [bookingLocations, setBookingLocations] = useState([]);

  const loadVisitTypesAndProviders = async () => {
    if (isDemo) {
      setAptTypes(DEMO_APT_TYPES);
      setProviders(DEMO_PROVIDERS);
      setBookingLocations([{ id: 'main', name: 'Main Office' }, { id: 'north', name: 'North Clinic' }]);
      // Seed the default provider so the downstream availability call carries
      // entity_id even if the user never opens the provider dropdown.
      if (!selectedProvider) setSelectedProvider(DEMO_PROVIDERS[0]);
      if (!selectedLocation) setSelectedLocation('main');
      return;
    }
    setTypesLoading(true); setTypesError(null);
    const [typesRes, entitiesRes, locationsRes] = await Promise.all([
      schedulerApi.listVisitTypes({ active: true }),
      schedulerApi.listEntities({ active: true }),
      schedulerApi.listLocations({ active: true }),
    ]);
    if (!typesRes.ok) {
      setTypesError(typesRes.error);
      setTypesLoading(false);
      return;
    }
    setAptTypes((typesRes.data?.visit_types || typesRes.data?.items || []).map(normalizeVisitType));
    if (entitiesRes.ok) {
      const rawEnts = entitiesRes.data?.entities || entitiesRes.data?.items || (Array.isArray(entitiesRes.data) ? entitiesRes.data : []);
      const mappedProviders = rawEnts.map(normalizeEntity).filter((p) => p.id);
      setProviders(mappedProviders);
      // Seed default provider — priority:
      //   1) prefillProviderId from caller (slot grid click, etc.)
      //   2) first loaded provider
      // This guarantees the vendor §17 `entity_id` query param is ALWAYS
      // present on `/slots/available` calls and avoids the 422 surface.
      if (!selectedProvider) {
        const prefilled = prefillProviderId && mappedProviders.find((p) => p.id === prefillProviderId);
        setSelectedProvider(prefilled || mappedProviders[0] || null);
      }
    }
    if (locationsRes.ok) {
      const rawLocs = locationsRes.data?.locations || locationsRes.data?.items || (Array.isArray(locationsRes.data) ? locationsRes.data : []);
      const mappedLocs = rawLocs.map((l) => ({ id: l.id || l._id, name: l.name || l.location_name })).filter((l) => l.id && l.name);
      setBookingLocations(mappedLocs);
      // Seed default location — mirrors the provider seeding so both vendor §17
      // scope params are always populated before the availability call fires.
      if (!selectedLocation) {
        const prefilled = prefillLocationId && mappedLocs.find((l) => l.id === prefillLocationId);
        setSelectedLocation((prefilled && prefilled.id) || mappedLocs[0]?.id || '');
      }
    }
    setTypesLoading(false);
  };

  const loadAvailability = async (type, providerOverride, locationOverride) => {
    if (!type) return;
    if (isDemo) {
      setSlots(DEMO_SLOTS);
      return;
    }
    const provider = providerOverride || selectedProvider;
    const entityId = provider?.id || prefillProviderId;
    const locationId = (locationOverride ?? selectedLocation) || prefillLocationId;
    // Vendor §17 requires BOTH entity_id AND location_id — mirror the
    // schedulerApi guard here so the UI can surface a friendlier message
    // and keep the spinner off when scope is missing.
    if (!entityId || !locationId) {
      setSlotsError({
        code: 'MISSING_SCOPE',
        message: 'Select provider and location to load availability.',
      });
      setSlots([]);
      setSlotsLoading(false);
      return;
    }
    setSlotsLoading(true); setSlotsError(null);
    const params = {
      date: pickDate(selectedDate),
      visit_type_id: type.id,
      duration_min: customDuration || type.duration,
      entity_id: entityId,
      location_id: locationId,
    };
    const res = await schedulerApi.queryAvailability(params);
    setSlotsLoading(false);
    if (!res.ok) {
      setSlotsError(res.error);
      setSlots([]);
      return;
    }
    setSlots((res.data?.slots || []).map(normalizeSlot));
  };

  // Load visit types & providers when modal opens
  useEffect(() => {
    if (open) {
      loadVisitTypesAndProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDemo]);

  // Re-query availability when visit type OR provider changes.
  useEffect(() => {
    if (aptType && selectedProvider) loadAvailability(aptType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aptType?.id, customDuration, selectedProvider?.id, selectedLocation]);

  /* ═══ Real-mode patient search ════════════════════════════════════
   * Debounce the free-text search (300 ms), hit `/api/patients?search=…`,
   * normalize the response envelope into the same shape that MOCK_PATIENTS
   * uses so the select handler + result UI don't branch on mode.
   * ══════════════════════════════════════════════════════════════════ */
  const [realPatientResults, setRealPatientResults] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState(null);
  const [patientSearchHydrated, setPatientSearchHydrated] = useState(false);

  const normalizeSearchPatient = (raw) => ({
    id: raw.id || raw._id || raw.patient_id || '',
    name: raw.name || raw.full_name || `${raw.first_name || ''} ${raw.last_name || ''}`.trim(),
    dob: raw.dob || raw.date_of_birth || raw.birthdate || '',
    phone: raw.phone || raw.phone_number || raw.mobile || '',
    insurance: raw.insurance?.status || raw.insurance_status || (raw.insurance?.payer ? 'Active' : ''),
    lastVisit: raw.last_visit || raw.last_visit_at || '',
    noShowRisk: raw.no_show_risk ?? null,
    balance: raw.balance ?? raw.outstanding_balance ?? 0,
  });

  useEffect(() => {
    if (!open) return;
    // Demo mode uses the in-memory MOCK_PATIENTS filter; no network.
    if (isDemo) return;
    // Real mode: require at least 2 chars before hitting the API.
    if (search.length < 2) {
      setRealPatientResults([]);
      setPatientSearchError(null);
      setPatientSearchHydrated(false);
      return;
    }
    let cancelled = false;
    setPatientSearchLoading(true);
    setPatientSearchError(null);
    const handle = setTimeout(async () => {
      const res = await patientApi.searchPatients({ query: search, limit: 20 });
      if (cancelled) return;
      setPatientSearchLoading(false);
      setPatientSearchHydrated(true);
      if (!res.ok) {
        setPatientSearchError(res.error);
        setRealPatientResults([]);
        return;
      }
      const raw = res.data?.patients || res.data?.items || (Array.isArray(res.data) ? res.data : []);
      setRealPatientResults(raw.map(normalizeSearchPatient).filter((p) => p.id && p.name));
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, open, isDemo]);

  const filteredPatients = isDemo
    ? (search.length >= 2
        ? MOCK_PATIENTS.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || p.phone.includes(search))
        : [])
    : realPatientResults;

  const handleSelectPatient = (p) => {
    setSelectedPatient(p);
    setStep(2);
  };

  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    setSelectedProvider(providers.find(p => p.id === slot.provider) || { id: slot.provider, name: slot.provider });
    setStep(3);
  };

  const handleConfirm = () => {
    guardWrite('Create appointment', async () => {
      if (isDemo) {
        setBooking(true);
        setTimeout(() => {
          setBooking(false);
          toast.success('Appointment confirmed', {
            description: `${selectedPatient.name} — ${selectedSlot.time} with ${selectedProvider?.name}`,
          });
          onOpenChange(false);
          setStep(1); setSearch(''); setSelectedPatient(null); setAptType(null);
          setSelectedProvider(null); setSelectedSlot(null); setIsWalkIn(false); setCustomDuration(null);
        }, 1200);
        return;
      }
      // Real mode: POST /api/scheduler/appointments
      setBooking(true);
      const entityIdForBooking = selectedProvider?.id || selectedSlot?.provider || prefillProviderId;
      const locationIdForBooking = selectedLocation || prefillLocationId;
      // Walk-ins have no pre-selected slot — pin start_datetime to "now"
      // so the vendor §18 appointment write succeeds. Non-walk-ins must
      // have a slot (the UI prevents confirm without one).
      const now = new Date();
      const walkinDurationMin = customDuration || aptType?.duration || 20;
      const walkinEnd = new Date(now.getTime() + walkinDurationMin * 60000);
      const startIso = selectedSlot?.rawStart || (isWalkIn ? now.toISOString() : null);
      const endIso = selectedSlot?.rawEnd || (isWalkIn ? walkinEnd.toISOString() : null);
      // Vendor §18 canonical payload now carries `{ date, time }` split
      // fields (HH:MM 24h). We still emit `start`/`start_datetime` for
      // back-compat with the existing local backend (the ECHO_SCHEDULER
      // reconciliation additively accepts both shapes).
      const startDate = startIso ? new Date(startIso) : null;
      const pad2 = (n) => String(n).padStart(2, '0');
      const dateStr = startDate
        ? `${startDate.getFullYear()}-${pad2(startDate.getMonth() + 1)}-${pad2(startDate.getDate())}`
        : null;
      const timeStr = startDate
        ? `${pad2(startDate.getHours())}:${pad2(startDate.getMinutes())}`
        : null;
      const res = await schedulerApi.createAppointment({
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.name,
        entity_id: entityIdForBooking,
        // Vendor §18 appointments carry both scope params; mirror the slot
        // availability contract so the write lands against the right
        // entity/location pair.
        location_id: locationIdForBooking,
        visit_type_id: aptType?.id,
        visit_type: aptType?.label,
        // Vendor-canonical split fields (2026-05-04 spec).
        date: dateStr,
        time: timeStr,
        // Back-compat ISO fields — local backend still accepts these.
        start: startIso,
        start_datetime: startIso,
        end: endIso,
        end_datetime: endIso,
        // Vendor-canonical field is `is_walkin`. Keep the legacy
        // `walk_in` alongside for back-compat with older local backends.
        is_walkin: isWalkIn,
        walk_in: isWalkIn,
        telehealth,
        duration_override: customDuration || null,
      });
      setBooking(false);
      if (res.ok) {
        toast.success('Appointment confirmed', {
          description: `${selectedPatient.name} — ${selectedSlot.time} with ${selectedProvider?.name}`,
        });
        onOpenChange(false);
        setStep(1); setSearch(''); setSelectedPatient(null); setAptType(null);
        setSelectedProvider(null); setSelectedSlot(null); setIsWalkIn(false); setCustomDuration(null);
      } else {
        toast.error('Booking failed', { description: res.error?.message });
      }
    }, isDemo);
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(1); setSearch(''); setSelectedPatient(null); setAptType(null);
    setSelectedProvider(null); setSelectedSlot(null); setIsWalkIn(false); setCustomDuration(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="booking-modal">
        {/* Step indicator */}
        <div className="flex items-center gap-0 px-6 pt-5 pb-3">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center">
              <div className={cn("h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors",
                step >= s ? "bg-[#111] text-white" : "bg-slate-100 text-slate-400"
              )} data-testid={`booking-step-${s}`}>{step > s ? <CheckCircle2 className="h-3 w-3" /> : s}</div>
              {s < 3 && <div className={cn("h-0.5 w-12 mx-1 rounded-full transition-colors", step > s ? "bg-[#111]" : "bg-slate-200")} />}
            </div>
          ))}
          <span className="ml-3 text-[10px] text-slate-400">{step === 1 ? 'Patient' : step === 2 ? 'Appointment' : 'Confirm'}</span>
        </div>

        {/* ═══ STEP 1: PATIENT ═══ */}
        {step === 1 && (
          <div className="px-6 pb-5 space-y-3" data-testid="booking-step-patient">
            <DialogHeader className="p-0">
              <DialogTitle className="text-base font-semibold">Select Patient</DialogTitle>
              <DialogDescription className="text-xs">Search by name, DOB, or phone number</DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input type="text" placeholder="Search patients..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 transition-colors"
                autoFocus data-testid="booking-patient-search" />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1" data-testid="booking-patient-results">
              {/* Demo-mode states */}
              {isDemo && search.length < 2 && <p className="text-xs text-slate-400 text-center py-4">Type at least 2 characters</p>}
              {isDemo && search.length >= 2 && filteredPatients.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-slate-400 mb-2">No patients found</p>
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => toast.info('Add new patient flow')} data-testid="booking-add-patient">
                    <User className="h-3 w-3" /> Add New Patient
                  </Button>
                </div>
              )}

              {/* Real-mode states (wired to /api/patients?search=) */}
              {!isDemo && search.length < 2 && (
                <p className="text-xs text-slate-400 text-center py-4" data-testid="patient-search-hint">Type at least 2 characters to search patients</p>
              )}
              {!isDemo && search.length >= 2 && patientSearchLoading && (
                <div className="flex items-center justify-center gap-2 py-6" data-testid="patient-search-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                  <span className="text-xs text-slate-500">Searching patients…</span>
                </div>
              )}
              {!isDemo && search.length >= 2 && !patientSearchLoading && patientSearchError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2" data-testid="patient-search-error">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] text-red-800 font-medium">Patient search failed</p>
                    <p className="text-[10px] text-red-700 mt-0.5">{patientSearchError.message || 'Try again in a moment.'}</p>
                  </div>
                </div>
              )}
              {!isDemo && search.length >= 2 && !patientSearchLoading && !patientSearchError && patientSearchHydrated && filteredPatients.length === 0 && (
                <div className="text-center py-4" data-testid="patient-search-empty">
                  <p className="text-xs text-slate-400 mb-2">No patients match &ldquo;{search}&rdquo;</p>
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => toast.info('Add new patient flow')} data-testid="booking-add-patient">
                    <User className="h-3 w-3" /> Add New Patient
                  </Button>
                </div>
              )}

              {/* Unified results list — same render for demo + real */}
              {filteredPatients.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelectPatient(p)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                  style={{ background: 'none', cursor: 'pointer' }}
                  data-testid={`booking-patient-${p.id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User className="h-3.5 w-3.5 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{p.name}</p>
                    <p className="text-[10px] text-slate-400">DOB: {p.dob} · {p.phone}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={cn("text-[8px] border-0", p.insurance === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>{p.insurance}</Badge>
                      <span className="text-[9px] text-slate-400">Last visit: {p.lastVisit}</span>
                      {p.noShowRisk > 25 && <Badge className="text-[8px] border-0 bg-red-50 text-red-600">{p.noShowRisk}% risk</Badge>}
                      {p.balance > 0 && <Badge variant="outline" className="text-[8px] border-amber-200 text-amber-600">${p.balance} balance</Badge>}
                    </div>
                    {p.careGap && <p className="text-[9px] text-violet-600 mt-0.5"><Sparkles className="h-2.5 w-2.5 inline mr-0.5" />{p.careGap}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ═══ STEP 2: APPOINTMENT ═══ */}
        {step === 2 && (
          <div className="px-6 pb-5 space-y-4" data-testid="booking-step-appointment">
            <DialogHeader className="p-0">
              <DialogTitle className="text-base font-semibold">Schedule Appointment</DialogTitle>
              <DialogDescription className="text-xs">For {selectedPatient?.name}</DialogDescription>
            </DialogHeader>

            {/* Provider (required — drives the entity_id query param for §17 availability) */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Provider</p>
              {providers.length === 0 ? (
                <p className="text-[10px] text-slate-400 py-2" data-testid="booking-providers-empty">No providers available.</p>
              ) : (
                <select
                  value={selectedProvider?.id || ''}
                  onChange={(e) => setSelectedProvider(providers.find((p) => p.id === e.target.value) || null)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400 transition-colors bg-white"
                  data-testid="booking-provider-select"
                >
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.specialty ? ` — ${p.specialty}` : ''}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Location (required — drives the location_id query param for §17 availability) */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Location</p>
              {bookingLocations.length === 0 ? (
                <p className="text-[10px] text-slate-400 py-2" data-testid="booking-locations-empty">No locations available.</p>
              ) : (
                <select
                  value={selectedLocation || ''}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400 transition-colors bg-white"
                  data-testid="booking-location-select"
                >
                  {bookingLocations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Appointment Type */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Appointment Type</p>
              {typesLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid="apt-types-loading">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading visit types…
                </div>
              )}
              {!typesLoading && typesError && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-100" data-testid="apt-types-error">
                  <AlertTriangle className="h-3 w-3 text-red-500" />
                  <span className="text-[10px] text-red-700 flex-1">{typesError.message || 'Failed to load'}</span>
                  <button onClick={loadVisitTypesAndProviders} className="text-[10px] text-red-700 underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
                </div>
              )}
              {!typesLoading && !typesError && aptTypes.length === 0 && (
                <p className="text-[10px] text-slate-400 py-2" data-testid="apt-types-empty">No visit types configured.</p>
              )}
              {!typesLoading && !typesError && aptTypes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {aptTypes.map(t => (
                    <button key={t.id} onClick={() => setAptType(t)}
                      className={cn("px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                        aptType?.id === t.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`apt-type-${t.id}`}>
                      {t.label} <span className="text-slate-400 ml-1">{t.duration}m</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Telehealth toggle */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button onClick={() => setTelehealth(!telehealth)}
                  className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", telehealth ? "bg-violet-600" : "bg-slate-200")}
                  style={{ border: 'none', cursor: 'pointer' }} data-testid="telehealth-toggle">
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: telehealth ? 'translateX(18px)' : 'translateX(2px)' }} />
                </button>
                <span className="text-xs text-slate-600">Telehealth</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setIsWalkIn(!isWalkIn)}
                  className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", isWalkIn ? "bg-amber-500" : "bg-slate-200")}
                  style={{ border: 'none', cursor: 'pointer' }} data-testid="walkin-toggle">
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: isWalkIn ? 'translateX(18px)' : 'translateX(2px)' }} />
                </button>
                <span className="text-xs text-slate-600">Walk-in</span>
              </div>
            </div>

            {/* Duration override */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Duration</p>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border overflow-hidden">
                  {[15, 20, 30, 45, 60].map(d => (
                    <button key={d} onClick={() => setCustomDuration(d)}
                      className={cn("px-3 py-1.5 text-xs font-medium transition-colors",
                        (customDuration || aptType?.duration) === d ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                      )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`duration-${d}`}>{d}m</button>
                  ))}
                </div>
                <span className="text-[9px] text-slate-400">{aptType ? `Default: ${aptType.duration}m` : ''}</span>
              </div>
            </div>

            {/* Available slots */}
            {aptType && (
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Available Slots</p>
                {slotsLoading && (
                  <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid="slots-loading">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading availability…
                  </div>
                )}
                {!slotsLoading && slotsError && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-100" data-testid="slots-error">
                    <AlertTriangle className="h-3 w-3 text-red-500" />
                    <span className="text-[10px] text-red-700 flex-1">{slotsError.message || 'Failed to load slots'}</span>
                    <button onClick={() => loadAvailability(aptType)} className="text-[10px] text-red-700 underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
                  </div>
                )}
                {!slotsLoading && !slotsError && slots.length === 0 && (
                  <p className="text-[10px] text-slate-400 py-2" data-testid="slots-empty">No available slots for this visit type.</p>
                )}
                {!slotsLoading && !slotsError && slots.length > 0 && (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {slots.map((slot, i) => {
                      const prov = providers.find(p => p.id === slot.provider) || { name: slot.provider };
                      return (
                        <button key={i} onClick={() => handleSelectSlot(slot)}
                          className={cn("w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left",
                            slot.recommended ? "border-violet-200 bg-violet-50/50 hover:bg-violet-50" : "border-slate-100 hover:bg-slate-50"
                          )} style={{ cursor: 'pointer' }} data-testid={`slot-option-${i}`}>
                          <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <div className="flex-1">
                            <span className="text-xs font-medium text-slate-800">{slot.time}</span>
                            <span className="text-[10px] text-slate-400 ml-2">{prov?.name}</span>
                          </div>
                          {slot.recommended && (
                            <Badge className="text-[8px] border-0 bg-violet-100 text-violet-700 gap-0.5">
                              <Sparkles className="h-2 w-2" /> Recommended
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-2 gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setStep(1)}>Back</Button>
              {isWalkIn && aptType && selectedProvider && selectedLocation && (
                <Button
                  size="sm"
                  className="text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1.5"
                  data-testid="walkin-continue-btn"
                  onClick={() => {
                    // Walk-ins skip slot picking — jump straight to confirm.
                    setSelectedSlot(null);
                    setStep(3);
                  }}
                >
                  Continue as walk-in <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ═══ STEP 3: CONFIRM ═══ */}
        {step === 3 && (
          <div className="px-6 pb-5 space-y-4" data-testid="booking-step-confirm">
            <DialogHeader className="p-0">
              <DialogTitle className="text-base font-semibold">Confirm Appointment</DialogTitle>
            </DialogHeader>

            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{selectedPatient?.name}</p>
                  <p className="text-[10px] text-slate-400">DOB: {selectedPatient?.dob}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <div>
                  <p className="text-[9px] text-slate-400 uppercase">Date & Time</p>
                  <p className="text-xs font-medium text-slate-800" data-testid="confirm-datetime"><Calendar className="h-3 w-3 inline mr-1" />{selectedSlot?.time || (isWalkIn ? 'Walk-in (Now)' : '—')}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 uppercase">Provider</p>
                  <p className="text-xs font-medium text-slate-800">{selectedProvider?.name}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 uppercase">Type</p>
                  <p className="text-xs font-medium text-slate-800">{aptType?.label} ({customDuration || aptType?.duration}m){isWalkIn && <Badge className="text-[8px] border-0 bg-amber-50 text-amber-600 ml-1">Walk-in</Badge>}</p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 uppercase">Location</p>
                  <p className="text-xs font-medium text-slate-800"><MapPin className="h-3 w-3 inline mr-1" />{telehealth ? 'Telehealth' : (isDemo ? 'Main Office' : (selectedSlot?.location_name || '—'))}</p>
                </div>
                {selectedPatient?.balance > 0 && (
                  <div className="col-span-2 pt-2 border-t border-slate-100">
                    <Badge variant="outline" className="text-[9px] border-amber-200 text-amber-600"><DollarSign className="h-2.5 w-2.5 mr-0.5" />${selectedPatient.balance} prior balance</Badge>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <CheckCircle2 className="h-3 w-3 text-slate-400" /> Intake forms will be sent to patient
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <CheckCircle2 className="h-3 w-3 text-slate-400" /> Reminder sequence: 48h, 24h, 2h before
            </div>

            {!allowBooking && (
              <div className="p-2 rounded-lg bg-amber-50 border border-amber-100 flex items-start gap-1.5" data-testid="booking-readonly-banner">
                <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-700 leading-tight">Your role doesn't permit booking. Ask a Front Desk or Admin to complete this appointment.</p>
              </div>
            )}

            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setStep(2)}>Back</Button>
              <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222] gap-1.5" onClick={handleConfirm} disabled={booking || !allowBooking} data-testid="booking-confirm-btn">
                {booking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                {booking ? 'Booking...' : 'Confirm Appointment'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
